import { useEffect } from "react";
import { motion } from "framer-motion";
import { PenLine, Phone, Instagram, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/lib/site-config";
import { useInView } from "@/hooks/use-in-view";

/**
 * Customer reviews.
 *
 * When `siteConfig.widgets.reviewIframeSrc` is set, the Reputation Hub widget
 * is embedded at its original height — no clipping, no forced max-height.
 *
 * Until F&T Renovations' own review profile is connected we deliberately show
 * no testimonials rather than placeholder ones: inventing reviews for a real
 * contractor would be misleading to homeowners. The section instead invites
 * past customers to leave the first one.
 */
const ReviewWidget = () => {
  const embedSrc = siteConfig.widgets.reviewIframeSrc;
  // The reviews widget always sits well below the fold, so neither its script
  // nor its iframe is fetched until a visitor scrolls towards it.
  const { ref: wrapRef, inView } = useInView<HTMLDivElement>("400px");

  useEffect(() => {
    if (!embedSrc || !inView) return;
    const scriptId = "reputation-hub-review-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.reviewScriptSrc;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
  }, [embedSrc, inView]);

  const reviewHref =
    siteConfig.social.google ??
    `${siteConfig.emailHref}?subject=${encodeURIComponent(
      `My review of ${siteConfig.name}`
    )}&body=${encodeURIComponent(
      "What we did for you:\n\nHow it went:\n\nWould you recommend us?\n"
    )}`;

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </motion.h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Most of our work in metro Atlanta comes from referrals and repeat customers — which is
            exactly why we give 10% back on jobs over $2,000.
          </p>
        </div>

        {embedSrc ? (
          <motion.div
            ref={wrapRef}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="review-widget-wrap max-w-5xl mx-auto"
          >
            {inView && (
              <iframe
                className="lc_reviews_widget"
                src={embedSrc}
                style={{ minWidth: "100%", width: "100%", border: "none", display: "block" }}
                title="Customer reviews"
                loading="lazy"
              />
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bento-card text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                <MessageSquare className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl text-foreground font-bold tracking-tight mb-3">
                Worked With Us? Tell People How It Went.
              </h3>
              <p className="text-muted-foreground max-w-xl mx-auto mb-7">
                We're building our review profile the same way we built the business — one honest
                job at a time. If {siteConfig.name} has done work for you, we'd genuinely
                appreciate you sharing it.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={reviewHref} target={siteConfig.social.google ? "_blank" : undefined} rel="noopener noreferrer">
                  <Button size="lg" className="gap-2 rounded-xl font-semibold">
                    <PenLine className="h-4 w-4" /> Write Us a Review
                  </Button>
                </a>
                {siteConfig.social.instagram && (
                  <a href={siteConfig.social.instagram} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="gap-2 rounded-xl font-semibold">
                      <Instagram className="h-4 w-4" /> See Our Work
                    </Button>
                  </a>
                )}
                <a href={siteConfig.phoneHref} className="inline-flex w-fit">
                  <Button
                    size="lg"
                    className="gap-2 rounded-xl font-semibold bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    <Phone className="h-4 w-4" /> {siteConfig.phone}
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ReviewWidget;
